import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCalendar, FiPhone, FiClock, FiDollarSign, FiStar, FiVideo, FiXCircle, FiEye } from 'react-icons/fi';

const API_BASE = `${process.env.REACT_APP_API_URL}/api`;

function BookingsView({ userId }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchBookings();
  }, [userId, filter]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const url = filter === 'all' 
        ? `${API_BASE}/bookings/?user_id=${userId}`
        : `${API_BASE}/bookings/?user_id=${userId}&status=${filter}`;
      
      const response = await axios.get(url);
      if (response.data.success) {
        setBookings(response.data.data);
        setError(null);
      }
    } catch (err) {
      setError('Failed to load bookings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    
    try {
      const response = await axios.delete(`${API_BASE}/bookings/?id=${bookingId}&user_id=${userId}`);
      if (response.data.success) {
        setBookings(bookings.map(b => b.id === bookingId ? {...b, status: 'cancelled'} : b));
      }
    } catch (err) {
      alert('Failed to cancel booking');
    }
  };

  const statusColors = {
    scheduled: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="text-gray-500 text-sm font-medium">Loading bookings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="bg-red-50 text-red-600 px-6 py-4 rounded-2xl text-sm font-medium">
          {error}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <FiCalendar className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-primary">My Bookings</h2>
        </div>
        <p className="text-gray-500 text-sm ml-[52px]">View and manage your scheduled sessions</p>
      </motion.div>

      {/* Filter Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="flex flex-wrap gap-2"
      >
        {['all', 'scheduled', 'completed', 'cancelled'].map((status) => (
          <button
            key={status}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              filter === status
                ? 'bg-primary text-white shadow-md shadow-primary/20'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => setFilter(status)}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </motion.div>

      {/* Bookings List */}
      <AnimatePresence mode="wait">
        {bookings.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-card p-12 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <FiCalendar className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-gray-500 text-sm">
              No {filter !== 'all' ? filter : ''} bookings found
            </p>
          </motion.div>
        ) : (
          <motion.div
            key="list"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-4"
          >
            {bookings.map((booking) => (
              <motion.div
                key={booking.id}
                variants={cardVariants}
                layout
                className="bg-white rounded-2xl shadow-card overflow-hidden hover:shadow-lg transition-shadow duration-300"
              >
                {/* Card Header */}
                <div className="flex items-center justify-between p-5 pb-4 border-b border-gray-100">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{booking.lawyer_name}</h3>
                    <p className="text-sm text-accent font-medium mt-0.5">{booking.specialization}</p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase ${
                      statusColors[booking.status] || 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {booking.status.toUpperCase()}
                  </span>
                </div>

                {/* Card Body */}
                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <FiCalendar className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium">Date & Time</p>
                      <p className="text-sm text-gray-700 font-medium">
                        {new Date(booking.consultation_date).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                      <FiPhone className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium">Type</p>
                      <p className="text-sm text-gray-700 font-medium">
                        {booking.service_type.replace('-', ' ').toUpperCase()}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <FiClock className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium">Duration</p>
                      <p className="text-sm text-gray-700 font-medium">{booking.duration_minutes} minutes</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                      <FiDollarSign className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium">Amount</p>
                      <p className="text-sm text-gray-700 font-medium">₹{booking.amount.toFixed(2)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-yellow-50 flex items-center justify-center flex-shrink-0">
                      <FiStar className="w-4 h-4 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium">Rating</p>
                      <p className="text-sm text-gray-700 font-medium">
                        {booking.rating}/5 • ₹{booking.hourly_rate}/hr
                      </p>
                    </div>
                  </div>
                </div>

                {/* Meeting Link */}
                {booking.meeting_link && (
                  <div className="px-5 pb-2">
                    <a
                      href={booking.meeting_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/5 text-primary text-sm font-medium hover:bg-primary/10 transition-colors"
                    >
                      <FiVideo className="w-4 h-4" />
                      Join Meeting
                    </a>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3 px-5 py-4 border-t border-gray-100">
                  {booking.status === 'scheduled' && (
                    <button
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 transition-colors"
                      onClick={() => handleCancelBooking(booking.id)}
                    >
                      <FiXCircle className="w-4 h-4" />
                      Cancel Booking
                    </button>
                  )}
                  <button className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">
                    <FiEye className="w-4 h-4" />
                    View Details
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default BookingsView;
